import { Fragment, useId } from 'react';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';

import { HelpLabel } from './InfoTip';

/* A labelled control. The label is wired to the input by a generated id, not by nesting.
 *
 * `useId` rather than a caller-supplied string, because the failure this prevents is silent: two
 * instances of the same form on one screen produce duplicate ids, `htmlFor` then points at whichever
 * came first, and clicking the second label focuses the first input. Nothing looks wrong.
 *
 * `hint` is wired through `aria-describedby` rather than merely rendered nearby. On the connection
 * form the hint is "never a password" — text a sighted user reads before typing and a screen-reader
 * user would otherwise never hear, because a paragraph after an input is not announced with it.
 */

export interface FieldProps {
  readonly label: string;
  /* `string | undefined` rather than a bare optional. `exactOptionalPropertyTypes` is on, so a
   * caller forwarding its own optional `hint` cannot pass explicit `undefined` to `hint?: string` —
   * and every wrapper below does exactly that. */
  readonly hint?: string | undefined;
  /** A short explanation of the term, behind an "i" beside the label. See `InfoTip`. */
  readonly help?: string | undefined;
  /** Lays the label beside the control rather than above it, for a toolbar row.
   *
   * `.lq-field--inline` also zeroes the 14px `margin-bottom` a stacked field carries to space
   * itself from the next one in a form. That margin is part of the flex item's *margin box*, so in
   * a toolbar aligned on `flex-end` it lifts the control's visible bottom above everything beside
   * it — which is how a field in a row of chips ends up looking a line too high even when it is on
   * the same line.
   */
  readonly inline?: boolean;
  /** What is wrong with the value, said beside the field rather than under a submit button.
   *
   * Wired three ways, each for a different reader: the text is in `aria-describedby` so a screen
   * reader hears it with the field, `aria-invalid` marks the control so it is announced as invalid,
   * and the wrapper's modifier draws the boundary in the error colour for everybody else. Colour is
   * never the only signal — the sentence is always rendered. */
  readonly error?: string | undefined;
  /** Receives the id, the describedby and the invalid flag, so the caller cannot forget to wire
   * them. */
  readonly children: (props: {
    readonly id: string;
    readonly 'aria-describedby': string | undefined;
    readonly 'aria-invalid': true | undefined;
  }) => ReactNode;
}

export function Field({
  label,
  hint,
  help,
  inline = false,
  error,
  children,
}: FieldProps): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = error !== undefined && error !== '';
  const describedBy =
    [hint === undefined ? null : hintId, invalid ? errorId : null]
      .filter((each): each is string => each !== null)
      .join(' ') || undefined;

  return (
    <div
      className={[
        'lq-field',
        inline ? 'lq-field--inline' : '',
        invalid ? 'lq-field--invalid' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* The icon beside the label rather than inside it: a button in a `<label>` joins the input's
          accessible name and focuses the input when pressed. */}
      <HelpLabel subject={label} help={help}>
        <label className="lq-field__label" htmlFor={id}>
          {label}
        </label>
      </HelpLabel>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': invalid ? true : undefined,
      })}
      {hint !== undefined && (
        <p className="lq-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {invalid ? (
        <p className="lq-field__error" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: string | undefined;
  readonly placeholder?: string | undefined;
  readonly type?: 'text' | 'search' | 'number';
  readonly required?: boolean;
  readonly maxLength?: number | undefined;
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  /** See `FieldProps.help`. */
  readonly help?: string | undefined;
  /** See `FieldProps.inline`. */
  readonly inline?: boolean;
  /** See `FieldProps.error`. */
  readonly error?: string | undefined;
  /** Monospace, for a value read character by character — a host, a port, an identifier. */
  readonly mono?: boolean;
  /** Turns the browser's own suggestions off for a value that is not the reader's own data. */
  readonly autoComplete?: 'off' | undefined;
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  placeholder,
  type = 'text',
  required = false,
  maxLength,
  min,
  max,
  help,
  inline = false,
  error,
  mono = false,
  autoComplete,
}: TextFieldProps): ReactElement {
  return (
    <Field label={label} hint={hint} help={help} inline={inline} error={error}>
      {(wiring) => (
        <input
          {...wiring}
          className={mono ? 'lq-input lq-input--mono' : 'lq-input'}
          autoComplete={autoComplete}
          spellCheck={mono ? false : undefined}
          type={type}
          value={value}
          required={required}
          maxLength={maxLength}
          min={min}
          max={max}
          placeholder={placeholder}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
          }}
        />
      )}
    </Field>
  );
}

export interface CheckboxFieldProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly hint?: string | undefined;
}

/** A boolean, with its label beside it rather than above it.
 *
 * **Not `Field` with a raw checkbox inside**, and the reason was measured rather than guessed:
 * `.lq-field` is `display: flex; flex-direction: column`, whose children stretch to full width by
 * default, so the platform's first checkbox rendered **928px wide and 13px tall** — a full-width
 * sliver that a click at its own centre missed. Found in a real browser on the model-routing screen;
 * no earlier screen had used one, so nothing had exercised that combination.
 *
 * The label is sentence case beside the box rather than the uppercase `.lq-field__label` above it.
 * An uppercase label above a lone checkbox reads as a section heading with an unlabelled control,
 * and the clickable target is then the box alone instead of the box and its text.
 */
export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
}: CheckboxFieldProps): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="lq-field">
      <div className="lq-checkbox">
        <input
          id={id}
          type="checkbox"
          className="lq-checkbox__input"
          checked={checked}
          aria-describedby={hint === undefined ? undefined : hintId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.checked);
          }}
        />
        <label className="lq-checkbox__label" htmlFor={id}>
          {label}
        </label>
      </div>
      {hint !== undefined && (
        <p className="lq-field__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

export interface SelectFieldProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** `group` renders an `<optgroup>`: consecutive options sharing one are grouped under it, so a
   * long list reads as categories in the native control — keyboard and screen-reader behaviour
   * included, which a custom grouped menu would have to rebuild. */
  readonly options: readonly {
    readonly value: T;
    readonly label: string;
    readonly group?: string | undefined;
  }[];
  readonly hint?: string | undefined;
  /** See `FieldProps.help`. */
  readonly help?: string | undefined;
}

type SelectOption<T extends string> = SelectFieldProps<T>['options'][number];

/** Consecutive options sharing a `group`, in order. An option with none stands alone. */
function runsOf<T extends string>(
  options: readonly SelectOption<T>[],
): readonly {
  readonly group: string | undefined;
  readonly options: SelectOption<T>[];
}[] {
  const runs: { group: string | undefined; options: SelectOption<T>[] }[] = [];
  for (const option of options) {
    const last = runs.at(-1);
    if (last !== undefined && option.group !== undefined && last.group === option.group) {
      last.options.push(option);
    } else {
      runs.push({ group: option.group, options: [option] });
    }
  }
  return runs;
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
  help,
}: SelectFieldProps<T>): ReactElement {
  return (
    <Field label={label} hint={hint} help={help}>
      {(wiring) => (
        <select
          {...wiring}
          className="lq-input"
          value={value}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            /* The cast is confined to this one line and is safe by construction: the only values the
             * element can hold are the ones rendered from `options`, which are typed `T`. */
            onChange(event.target.value as T);
          }}
        >
          {runsOf(options).map((run, index) => {
            const rendered = run.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ));
            return run.group === undefined ? (
              <Fragment key={`run:${String(index)}`}>{rendered}</Fragment>
            ) : (
              <optgroup key={`group:${run.group}`} label={run.group}>
                {rendered}
              </optgroup>
            );
          })}
        </select>
      )}
    </Field>
  );
}

export interface SecretFieldProps {
  readonly label: string;
  /* **No `value` prop, deliberately.** See the paragraph below: `defaultValue` *is* the DOM `value`
   * attribute, so handing this component the live value writes the credential into the serialised HTML
   * on every keystroke — which is the exact exposure the uncontrolled shape exists to remove. The
   * caller keeps the value in its own state, fed by `onChange`, and clears the field with `resetKey`. */
  readonly onChange: (value: string) => void;
  readonly hint?: string | undefined;
  readonly placeholder?: string | undefined;
  readonly required?: boolean;
  readonly multiline?: boolean;
  /** Change this to clear the field. The input is uncontrolled, so a remount is what empties it. */
  readonly resetKey?: string | number;
  /** See `FieldProps.error`. Names the problem, never the value. */
  readonly error?: string | undefined;
}

/** A control for a value that must not be read off the screen, autofilled, or remembered.
 *
 * **In the kit rather than in the one screen that needs it**, because every protection here is a
 * browser-level attribute somebody would otherwise have to remember per input, and the failure of
 * forgetting is invisible: the field works perfectly and the secret is in the password manager, the
 * autofill store and any screenshot.
 *
 * `type="password"` keeps it off the screen. `autoComplete="off"` — plus `new-password`, which is what
 * Chrome actually honours where `off` is ignored on a login-shaped field — keeps it out of the
 * browser's saved credentials, which matters because this is a *service account's* password being
 * typed by an engineer whose own manager should not learn it. `spellCheck` and the capitalise and
 * correct attributes are off because a token is not prose and iOS would otherwise mangle it.
 *
 * `multiline` renders a textarea for a PEM block, which is the one credential shape that does not fit
 * on a line. Still `autoComplete="off"`; a textarea has no `type="password"`, so it is the one field
 * here whose value is visible — stated rather than papered over, because a PEM private key pasted from
 * a file is not a value somebody reads back off a screen and masking it would prevent them checking
 * they pasted the whole block.
 *
 * **This input is deliberately uncontrolled, and that is a security decision rather than a style
 * one.** React mirrors a *controlled* input's value into the DOM `value` **attribute**, so
 * `document.documentElement.outerHTML` contains the typed credential in plaintext — measured in a real
 * browser with real keystrokes, not inferred. Anything that reads the DOM reads it: an extension, an
 * error reporter that snapshots the page, somebody pasting outerHTML into a bug report.
 *
 * Stripping the attribute in an effect was tried first and **cannot work**: React re-applies it from
 * `restoreControlledState` at the end of the discrete event, after passive effects, so the framework
 * always wins the race. A timer scheduled to run later would win today and stop winning on a React
 * upgrade, with a docstring still claiming the property — which is worse than not having it.
 *
 * Uncontrolled removes the race instead of winning it — the browser does not reflect typed text into
 * the attribute, verified directly against a bare `<input type="password">` on this page.
 *
 * **But uncontrolled is not enough on its own, and the first attempt got this wrong.** It passed the
 * live value as `defaultValue`, and `defaultValue` *is* the `value` attribute — so the credential went
 * straight back into the serialised HTML on every keystroke, with the component now merely claiming
 * otherwise. Measured again and still present, which is what caught it. So this component takes no
 * value at all: the DOM holds the text in the property only, and the caller keeps its own copy in
 * React state from `onChange`. That copy is memory, and memory was never the exposure.
 *
 * The cost is that a caller can clear the field and cannot set it: `resetKey` remounts the input and a
 * remount is empty. That is the only programmatic write this form needs — after a successful
 * registration, and on a provider change — and it is a deliberate bound rather than a limitation
 * worked around: a component that could push a credential *into* an input is one that could restore
 * one from somewhere it should not have been kept.
 */
export function SecretField({
  label,
  onChange,
  hint,
  placeholder,
  required = false,
  multiline = false,
  resetKey = '',
  error,
}: SecretFieldProps): ReactElement {
  return (
    <Field label={label} hint={hint} error={error}>
      {(wiring) =>
        multiline ? (
          <textarea
            {...wiring}
            key={resetKey}
            className="lq-input lq-input--area"
            required={required}
            placeholder={placeholder}
            rows={4}
            autoComplete="off"
            spellCheck={false}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              onChange(event.target.value);
            }}
          />
        ) : (
          <input
            {...wiring}
            key={resetKey}
            className="lq-input"
            type="password"
            required={required}
            placeholder={placeholder}
            autoComplete="new-password"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              onChange(event.target.value);
            }}
          />
        )
      }
    </Field>
  );
}
